import type { Rect, V2 } from '../../core/math';
import { Rng } from '../../core/rng';
import type { StaticDef } from '../../physics/types';
import type {
  LoosePropDecor,
  Decor,
  FaceDecor,
  PropDecor,
  SignalHeadDecor,
  WallDecor,
  WallKind,
  ZebraDecor,
} from '../decor';
import {
  convexOverlap,
  obbAxes,
  obbCorners,
  obbPoint,
  offsetFace,
  pointInPolygon,
  pointSegmentDistance,
  polygonArea,
  polygonSegmentDistance,
  resampleClosed,
  simplifyClosed,
  type Obb,
} from '../geom';
import type {
  BuildingLot,
  BuildingStyle,
  CityLayout,
  Intersection,
  MapFeature,
  ParkedSpot,
  PedPath,
  PointOfInterest,
  RoadEdge,
  RoadNode,
  SignalDef,
} from '../types';
import * as D from './data';
import { BAY_WALL_HEIGHT, bayFor, bayWalls, blocksBayApproach } from '../../garage/bay';
import { BILLBOARDS, CAFE_NAMES, FACTORY_NAMES, OFFICE_NAMES, SHOP_NAMES, WAREHOUSE_NAMES } from './names';

/**
 * Turns the authored Low Borough data into a full CityLayout plus visual decor. Deterministic for a
 * given seed; pure (no three.js) so it runs in vitest.
 */
export interface LowBoroughWorld {
  layout: CityLayout;
  decor: Decor;
}

interface EdgeMeta {
  kind: D.StreetKind;
  spec: D.StreetKindSpec;
  left: D.FrontageRule;
  right: D.FrontageRule;
  a: V2;
  b: V2;
  dir: V2;
  len: number;
}

const KIND_PRIORITY: Record<D.StreetKind, number> = { high: 0, arterial: 1, quay: 2, street: 3, narrow: 4, bridge: 5, alley: 6 };

function rectPoly(r: Rect): V2[] {
  return [
    { x: r.minX, z: r.minZ },
    { x: r.maxX, z: r.minZ },
    { x: r.maxX, z: r.maxZ },
    { x: r.minX, z: r.maxZ },
  ];
}

function inRect(r: Rect, p: V2, pad = 0): boolean {
  return p.x >= r.minX - pad && p.x <= r.maxX + pad && p.z >= r.minZ - pad && p.z <= r.maxZ + pad;
}

const leftOf = (d: V2): V2 => ({ x: d.z, z: -d.x });
const headingOf = (d: V2): number => Math.atan2(d.x, d.z);

const cache = new Map<number, LowBoroughWorld>();

/** The map's painted areas (world/types.ts MapFeature): the water clipped to the borough, and the zones by name. */
function mapFeatures(bounds: Rect): MapFeature[] {
  const clip = (r: Rect): Rect => ({ minX: Math.max(r.minX, bounds.minX), minZ: Math.max(r.minZ, bounds.minZ), maxX: Math.min(r.maxX, bounds.maxX), maxZ: Math.min(r.maxZ, bounds.maxZ) });
  const out: MapFeature[] = [
    { kind: 'water', rect: clip(D.CANAL), name: 'The Gullet' },
    { kind: 'water', rect: clip(D.BASIN), name: 'Gullet Basin' },
  ];
  const named: Record<string, [MapFeature['kind'], string]> = {
    viaduct: ['rail', 'Viaduct'],
    gasworks: ['yard', 'Gasworks'],
    'scrap yard': ['yard', 'Scrap yard'],
    'dock apron': ['yard', 'Dock apron'],
    'mercy square gardens': ['green', 'Mercy Square Gardens'],
    "st brannoc's churchyard": ['green', 'Churchyard'],
    'goods yard': ['yard', 'Goods Depot'],
    'kiln common': ['green', 'Kiln Common'],
    'estate site': ['site', 'Building site'],
  };
  for (const z of D.ZONES) {
    const n = named[z.reason];
    if (n) out.push({ kind: n[0], rect: clip(z.rect), name: n[1] });
  }
  return out;
}

export function buildLowBorough(seed = 1971): LowBoroughWorld {
  const hit = cache.get(seed);
  if (hit) return hit;
  const world = generate(seed);
  cache.set(seed, world);
  return world;
}

function generate(seed: number): LowBoroughWorld {
  const rng = new Rng(seed);
  // Kiln Common (the streets north of the old boundary) draws from its own stream, so the old district
  // is generated exactly as before it existed: the same lots, the same doors for the POIs.
  const rngK = rng.fork('kiln-common');
  const rngMain = rng;
  const B = D.BOUNDS;
  const CORE = D.CORE_BOUNDS;

  // ------------------------------------------------------------------ nodes & edges
  const keyToId = new Map<string, number>();
  const nodes: RoadNode[] = D.NODES.map(([k, x, z], i) => {
    keyToId.set(k, i);
    return { id: i, pos: { x, z }, intersectionId: null };
  });
  const nid = (k: string): number => {
    const v = keyToId.get(k);
    if (v === undefined) throw new Error(`Low Borough: unknown node '${k}'`);
    return v;
  };

  const edges: RoadEdge[] = [];
  const meta: EdgeMeta[] = [];
  const setts: boolean[] = [];
  for (const st of D.STREETS) {
    const spec = D.STREET_KINDS[st.kind];
    for (let i = 0; i < st.nodes.length - 1; i++) {
      const from = nid(st.nodes[i]!);
      const to = nid(st.nodes[i + 1]!);
      const a = nodes[from]!.pos;
      const b = nodes[to]!.pos;
      const len = Math.hypot(b.x - a.x, b.z - a.z);
      const dir = { x: (b.x - a.x) / len, z: (b.z - a.z) / len };
      const oneWay = !!st.oneWay;
      const lanesBackward = oneWay ? 0 : 1;
      const width = (1 + lanesBackward) * spec.laneWidth + 2 * spec.parking;
      const district = D.districtAt({ x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 });
      edges.push({
        id: edges.length,
        from,
        to,
        lanesForward: 1,
        lanesBackward,
        laneWidth: spec.laneWidth,
        width,
        speedLimit: spec.speed,
        oneWay,
        alley: st.kind === 'alley',
        main: D.MAIN_KINDS.has(st.kind),
        name: st.name,
        districtId: district,
        sidewalk: spec.sidewalk,
      });
      meta.push({
        kind: st.kind,
        spec,
        left: st.left ?? D.defaultFrontage(district, st.kind),
        right: st.right ?? D.defaultFrontage(district, st.kind),
        a,
        b,
        dir,
        len,
      });
      setts.push(!!st.setts);
    }
  }
  const incident: number[][] = nodes.map(() => []);
  for (const e of edges) {
    incident[e.from]!.push(e.id);
    incident[e.to]!.push(e.id);
  }
  // The original streets are a prefix of the edge list (Kiln Common's are appended and each has an end
  // outside the original rectangle).
  const inCore = (p: V2): boolean => inRect(CORE, p);
  const coreEdges = edges.findIndex((e) => !inCore(nodes[e.from]!.pos) || !inCore(nodes[e.to]!.pos));
  const CORE_EDGES = coreEdges < 0 ? edges.length : coreEdges;
  if (edges.slice(CORE_EDGES).some((e) => inCore(nodes[e.from]!.pos) && inCore(nodes[e.to]!.pos))) throw new Error('Low Borough: core streets must come first');
  /** Lot placement sees only the first `lotEdges` edges and stays inside `lotBounds` (see rngK). */
  let lotEdges = CORE_EDGES;
  let lotBounds: Rect = CORE;
  const otherEnd = (e: RoadEdge, n: number): number => (e.from === n ? e.to : e.from);
  const dirFrom = (n: number, e: RoadEdge): V2 => {
    const p = nodes[n]!.pos;
    const q = nodes[otherEnd(e, n)]!.pos;
    const l = Math.hypot(q.x - p.x, q.z - p.z) || 1;
    return { x: (q.x - p.x) / l, z: (q.z - p.z) / l };
  };
  const edgeBetween = (ka: string, kb: string): RoadEdge => {
    const a = nid(ka);
    const b = nid(kb);
    const e = edges.find((x) => (x.from === a && x.to === b) || (x.from === b && x.to === a));
    if (!e) throw new Error(`Low Borough: no edge ${ka}-${kb}`);
    return e;
  };

  // ------------------------------------------------------------------ intersections & signals
  const signalled = new Set(D.SIGNALLED.map(nid));
  const intersections: Intersection[] = [];
  // The old district's junctions first, in node order (their ids and signal offsets don't change), then
  // the ones Kiln Common's streets made or added (a corner like Kessmouth Road / Slag Road became a T).
  const coreJunction = (n: RoadNode): boolean => incident[n.id]!.filter((id) => id < CORE_EDGES).length >= 3;
  for (const pass of [true, false]) {
    for (const n of nodes) {
      const ids = incident[n.id]!;
      if (ids.length < 3 || coreJunction(n) !== pass) continue;
      const maxHalf = Math.max(...ids.map((id) => edges[id]!.width / 2));
      const radius = maxHalf + 3;
      let signal: SignalDef | null = null;
      if (signalled.has(n.id)) signal = makeSignal(n.id, ids, pass ? rng : rngK);
      n.intersectionId = intersections.length;
      intersections.push({ id: intersections.length, nodeId: n.id, pos: { ...n.pos }, radius, edgeIds: [...ids], signal });
    }
  }

  function makeSignal(nodeId: number, ids: number[], rng: Rng): SignalDef {
    const clusters: { ang: number; ids: number[]; major: boolean }[] = [];
    for (const id of ids) {
      const e = edges[id]!;
      const d = dirFrom(nodeId, e);
      let ang = Math.atan2(d.z, d.x);
      if (ang < 0) ang += Math.PI;
      if (ang >= Math.PI) ang -= Math.PI;
      const major = meta[id]!.kind === 'arterial' || meta[id]!.kind === 'high';
      let c = clusters.find((cl) => {
        let diff = Math.abs(cl.ang - ang);
        diff = Math.min(diff, Math.PI - diff);
        return diff < 0.5;
      });
      if (!c) {
        c = { ang, ids: [], major: false };
        clusters.push(c);
      }
      c.ids.push(id);
      c.major ||= major;
    }
    const phases = [];
    for (const c of clusters) {
      phases.push({ duration: c.major ? 14 : 10, green: [...c.ids], amber: [] });
      phases.push({ duration: 3, green: [], amber: [...c.ids] });
      phases.push({ duration: 1.5, green: [], amber: [] });
    }
    return { offset: Math.round(rng.range(0, 40) * 10) / 10, phases };
  }

  /** Building-line clearance at a node on one side of an edge (cross streets on that side). */
  function sideClearance(nodeId: number, e: RoadEdge, ns: V2, pad = 0.2): number {
    let c = 0;
    for (const id of incident[nodeId]!) {
      if (id === e.id || id >= lotEdges) continue;
      const o = edges[id]!;
      const d = dirFrom(nodeId, o);
      const dd = d.x * ns.x + d.z * ns.z;
      if (dd > 0.25) c = Math.max(c, (o.width / 2 + o.sidewalk) / Math.max(dd, 0.5) + pad);
    }
    return c;
  }

  // ------------------------------------------------------------------ lots
  let lots: BuildingLot[] = [];
  let lotPolys: V2[][] = [];
  let lotColliders: ('box' | 'circle' | 'none')[] = [];
  const colliders: StaticDef[] = [];
  const zonePolys = D.ZONES.map((z) => rectPoly(z.rect));

  function roadClear(poly: V2[], cx: number, cz: number, reach: number): boolean {
    for (let i = 0; i < lotEdges; i++) {
      const e = edges[i]!;
      const m = meta[i]!;
      const clr = e.alley ? e.width / 2 + 0.35 : e.width / 2 + e.sidewalk - 0.02;
      // far outside the edge's box grown by the reach: the distance test below would skip it too (boot time)
      const r = clr + reach + 1e-6;
      if (cx < Math.min(m.a.x, m.b.x) - r || cx > Math.max(m.a.x, m.b.x) + r || cz < Math.min(m.a.z, m.b.z) - r || cz > Math.max(m.a.z, m.b.z) + r) continue;
      if (pointSegmentDistance({ x: cx, z: cz }, m.a, m.b) > clr + reach) continue;
      if (polygonSegmentDistance(poly, m.a, m.b) < clr) return false;
    }
    return true;
  }

  function lotValid(obb: Obb): boolean {
    const poly = obbCorners(obb);
    const LB = lotBounds;
    for (const p of poly) if (p.x < LB.minX + 0.8 || p.x > LB.maxX - 0.8 || p.z < LB.minZ + 0.8 || p.z > LB.maxZ - 0.8) return false;
    for (const zp of zonePolys) if (convexOverlap(poly, zp, 0.01)) return false;
    if (!roadClear(poly, obb.center.x, obb.center.z, Math.hypot(obb.halfX, obb.halfZ))) return false;
    const box = polyBox(poly);
    for (const q of lotPolys) if (boxesTouch(box, polyBox(q)) && convexOverlap(poly, q, 0.02)) return false;
    if (lotEdges > CORE_EDGES && !seamFree(poly)) return false;
    return true;
  }

  /**
   * Bounding boxes of the lot polygons (kept per polygon: they are replaced, never changed). Two convex polygons
   * whose boxes are apart do not overlap, so convexOverlap with a margin >= 0 is false for them: skipping it
   * changes nothing (the layout generation's biggest cost at boot).
   */
  const boxes = new WeakMap<V2[], [number, number, number, number]>();
  function polyBox(poly: V2[]): [number, number, number, number] {
    let b = boxes.get(poly);
    if (!b) {
      b = [Infinity, Infinity, -Infinity, -Infinity];
      for (const p of poly) {
        if (p.x < b[0]) b[0] = p.x;
        if (p.z < b[1]) b[1] = p.z;
        if (p.x > b[2]) b[2] = p.x;
        if (p.z > b[3]) b[3] = p.z;
      }
      boxes.set(poly, b);
    }
    return b;
  }
  function boxesTouch(a: [number, number, number, number], b: [number, number, number, number]): boolean {
    const eps = 1e-6;
    return a[0] <= b[2] + eps && b[0] <= a[2] + eps && a[1] <= b[3] + eps && b[1] <= a[3] + eps;
  }

  /**
   * Kiln Common's lots never leave a slot a character can't fit through but the server's collider
   * slack would let a step cross (MOVE-40, docs/SECURITY.md): against every other lot and the boundary
   * wall the gap is either closed (< 0.3 m) or walkable (>= 0.9 m).
   */
  function seamFree(poly: V2[]): boolean {
    const bad = (gap: number): boolean => gap > 0.3 && gap < 0.9;
    const LB = lotBounds;
    for (const p of poly) {
      // The boundary wall is 2 m thick, centred on the bounds.
      if (bad(p.x - (LB.minX + 1)) || bad(LB.maxX - 1 - p.x) || bad(p.z - (LB.minZ + 1)) || bad(LB.maxZ - 1 - p.z)) return false;
    }
    for (const q of lotPolys) {
      if (!convexOverlap(poly, q, -1)) continue; // more than 1 m apart on some axis
      let gap = Infinity;
      for (let i = 0; i < q.length; i++) gap = Math.min(gap, polygonSegmentDistance(poly, q[i]!, q[(i + 1) % q.length]!));
      if (bad(gap)) return false;
    }
    return true;
  }

  function addLot(l: Omit<BuildingLot, 'id' | 'districtId'>, collider: 'box' | 'circle' | 'none' = 'box'): BuildingLot {
    const lot: BuildingLot = { ...l, id: lots.length, districtId: D.districtAt(l.center) };
    lots.push(lot);
    lotPolys.push(obbCorners(lot));
    lotColliders.push(collider);
    pushLotCollider(lot, collider);
    return lot;
  }

  function pushLotCollider(lot: BuildingLot, collider: 'box' | 'circle' | 'none'): void {
    if (collider === 'box') {
      colliders.push({ kind: 'box', center: { ...lot.center }, halfX: lot.halfX, halfZ: lot.halfZ, angle: lot.angle, tag: `building:${lot.id}` });
    } else if (collider === 'circle') {
      colliders.push({ kind: 'circle', center: { ...lot.center }, radius: Math.min(lot.halfX, lot.halfZ), tag: `building:${lot.id}` });
    }
  }

  /**
   * Core view: while true, the decor helpers see the old district as it was generated before Kiln
   * Common existed (only the core streets; the old lots Kiln Common's streets displaced still stand),
   * so the main random stream draws exactly as it always did and the old district's lamps, bins,
   * parking bays and signs come out the same. What that view places on a new street is then dropped.
   */
  let coreView = false;
  let removedLots: BuildingLot[] = [];
  const insideAnyLot = (p: V2, pad: number): boolean => {
    const list = coreView ? [...lots, ...removedLots] : lots;
    for (let i = 0; i < list.length; i++) {
      const l = list[i]!;
      if (Math.abs(p.x - l.center.x) > l.halfX + l.halfZ + pad + 1 || Math.abs(p.z - l.center.z) > l.halfX + l.halfZ + pad + 1) continue;
      const s = Math.sin(l.angle);
      const c = Math.cos(l.angle);
      const dx = p.x - l.center.x;
      const dz = p.z - l.center.z;
      const lx = dx * c - dz * s;
      const lz = dx * s + dz * c;
      if (Math.abs(lx) <= l.halfX + pad && Math.abs(lz) <= l.halfZ + pad) return true;
    }
    return false;
  };

  // Viaduct: arch spans between the road openings.
  const vx = D.VIADUCT.x;
  const openings: { z0: number; z1: number; kind: 'girder' | 'arch' | 'water' }[] = [];
  for (let i = 0; i < edges.length; i++) {
    const m = meta[i]!;
    const e = edges[i]!;
    if ((m.a.x - vx) * (m.b.x - vx) >= 0) continue;
    const t = (vx - m.a.x) / (m.b.x - m.a.x);
    const z = m.a.z + (m.b.z - m.a.z) * t;
    const half = e.alley ? e.width / 2 + 0.6 : e.width / 2 + e.sidewalk + 0.3;
    openings.push({ z0: z - half, z1: z + half, kind: e.alley ? 'arch' : 'girder' });
  }
  openings.push({ z0: D.CANAL.minZ - 0.5, z1: D.CANAL.maxZ + 0.5, kind: 'water' });
  openings.sort((a, b) => a.z0 - b.z0);
  const spans: { z0: number; z1: number }[] = [];
  {
    let z = -760;
    for (const o of openings) {
      if (o.z0 > z) spans.push({ z0: z, z1: o.z0 });
      z = Math.max(z, o.z1);
    }
    spans.push({ z0: z, z1: 760 });
  }
  for (const s of spans) {
    const z0 = Math.max(s.z0, B.minZ + 0.5);
    const z1 = Math.min(s.z1, B.maxZ - 0.5);
    if (z1 - z0 < 1) continue;
    addLot(
      {
        center: { x: vx, z: (z0 + z1) / 2 },
        halfX: (z1 - z0) / 2,
        halfZ: D.VIADUCT.halfWidth,
        angle: Math.PI / 2,
        height: D.VIADUCT.deckHeight,
        style: 'arches',
        tags: ['viaduct'],
        name: 'Kessmouth & District Railway',
      },
      'box',
    );
  }

  // Gas holders.
  const gasholders = [
    { center: { x: -92, z: -150 }, radius: 17, height: 24 },
    { center: { x: -90, z: -205 }, radius: 13, height: 19 },
  ];
  for (const g of gasholders) {
    addLot(
      { center: g.center, halfX: g.radius, halfZ: g.radius, angle: 0, height: g.height, style: 'gasholder', tags: ['round'], name: 'Kessmouth Gas Light & Coke Co.' },
      'circle',
    );
  }

  // Hand-placed landmark buildings (Kiln Common's come with its streets, below).
  for (const l of D.LANDMARKS) {
    if (!inCore(l.center)) continue;
    const lot = addLot({ center: l.center, halfX: l.halfX, halfZ: l.halfZ, angle: l.angle, height: l.height, style: l.style, tags: [...(l.tags ?? []), `key:${l.key}`], ...(l.name ? { name: l.name } : {}) });
    void lot;
  }

  // Frontage filler.
  const shopNames = rng.shuffle([...SHOP_NAMES]);
  const cafeNames = rng.shuffle([...CAFE_NAMES]);
  const officeNames = rng.shuffle([...OFFICE_NAMES]);
  const warehouseNames = rng.shuffle([...WAREHOUSE_NAMES]);
  const factoryNames = rng.shuffle([...FACTORY_NAMES]);
  let shopI = 0;
  let cafeI = 0;
  let officeI = 0;
  let whI = 0;
  let facI = 0;

  const HOUSE = 5.2;
  const byKind = (ids: number[]): number[] => ids.sort((a, b) => KIND_PRIORITY[meta[a]!.kind] - KIND_PRIORITY[meta[b]!.kind] || a - b);
  const frontage = (order: number[], rng: Rng): void => {
  for (const id of order) {
    const e = edges[id]!;
    const m = meta[id]!;
    for (const side of [1, -1] as const) {
      const rule = side === 1 ? m.left : m.right;
      if (rule.type !== 'lots') continue;
      const ns = { x: leftOf(m.dir).x * side, z: leftOf(m.dir).z * side };
      const garden = rule.garden ?? 0;
      const setback = e.alley ? e.width / 2 + 0.4 : e.width / 2 + e.sidewalk + garden;
      const t0 = sideClearance(e.from, e, ns);
      const t1 = m.len - sideClearance(e.to, e, ns);
      const [wmin, wmax] = rule.width ?? [10, 20];
      const [dmin, dmax] = rule.depth ?? [10, 14];
      const [hmin, hmax] = rule.height ?? [7, 12];
      let s = t0;
      let guard = 0;
      while (s < t1 - wmin * 0.5 && guard++ < 400) {
        const style: BuildingStyle = rule.weights ? rng.weighted(rule.styles, rule.weights) : rng.pick(rule.styles);
        let w = rng.range(wmin, wmax);
        if (style === 'terrace') {
          let n = Math.max(2, Math.round(w / HOUSE));
          if (s + n * HOUSE > t1) n = Math.floor((t1 - s) / HOUSE);
          if (n < 2) break;
          w = n * HOUSE;
        } else {
          if (s + w > t1) w = t1 - s;
          if (w < Math.max(5, wmin * 0.55)) break;
        }
        let depth = rng.range(dmin, dmax);
        if (style === 'shopfront' || style === 'cafe') depth = Math.min(depth, 14);
        const height = style === 'terrace' ? rng.range(hmin, hmax) : Math.round(rng.range(hmin, hmax) * 2) / 2;
        let placed = false;
        for (const [wf, df] of [
          [1, 1],
          [1, 0.72],
          [1, 0.5],
          [0.62, 1],
          [0.62, 0.6],
        ] as const) {
          let ww = w * wf;
          if (style === 'terrace') {
            const n = Math.max(2, Math.round(ww / HOUSE));
            ww = n * HOUSE;
          }
          const dd = Math.max(5, depth * df);
          const along = s + ww / 2;
          const c = {
            x: m.a.x + m.dir.x * along + ns.x * (setback + dd / 2),
            z: m.a.z + m.dir.z * along + ns.z * (setback + dd / 2),
          };
          const obb: Obb = { center: c, halfX: ww / 2, halfZ: dd / 2, angle: headingOf({ x: -ns.x, z: -ns.z }) };
          if (!lotValid(obb)) continue;
          const tags: string[] = [];
          let name: string | undefined;
          if (style === 'terrace') {
            tags.push(`units:${Math.round(ww / HOUSE)}`);
            if (garden > 0) tags.push('garden');
          } else if (style === 'shopfront') {
            name = shopNames[shopI++ % shopNames.length];
            if (rng.chance(0.55)) tags.push('awning');
          } else if (style === 'cafe') {
            name = cafeNames[cafeI++ % cafeNames.length];
            tags.push('awning', 'lit');
          } else if (style === 'office') {
            if (rng.chance(0.6)) name = officeNames[officeI++ % officeNames.length];
          } else if (style === 'warehouse') {
            if (rng.chance(0.7)) name = warehouseNames[whI++ % warehouseNames.length];
            if (rng.chance(0.5)) tags.push('hoist');
          } else if (style === 'factory') {
            if (rng.chance(0.7)) name = factoryNames[facI++ % factoryNames.length];
            tags.push('sawtooth');
            if (rng.chance(0.3)) tags.push('chimney');
          }
          addLot({ center: c, halfX: ww / 2, halfZ: dd / 2, angle: obb.angle, height, style, tags, ...(name ? { name } : {}) });
          const gap = style === 'terrace' || style === 'shopfront' || style === 'cafe' ? 0 : rng.range(1.5, 5);
          s += ww + gap;
          placed = true;
          break;
        }
        if (!placed) s += 3;
      }
    }
  }
  };
  frontage(byKind(edges.slice(0, CORE_EDGES).map((e) => e.id)), rng);

  // Backland: workshops, stores and lockup courts filling the deep block interiors.
  const BACKFILL: Record<string, { styles: BuildingStyle[]; weights: number[]; w: [number, number]; d: [number, number]; h: [number, number]; p: number }> = {
    'cinder-yards': { styles: ['shed', 'warehouse', 'lockup', 'factory'], weights: [4, 3, 2, 2], w: [12, 26], d: [8, 18], h: [4.5, 10], p: 0.6 },
    'the-parade': { styles: ['shed', 'warehouse', 'lockup', 'office'], weights: [3, 2, 2, 1], w: [8, 16], d: [6, 12], h: [4, 9], p: 0.55 },
    'tanners-row': { styles: ['lockup', 'shed'], weights: [3, 1], w: [10, 20], d: [6, 6.5], h: [2.8, 3.2], p: 0.12 },
    'mercy-square': { styles: ['lockup', 'shed', 'garage'], weights: [3, 2, 1], w: [8, 18], d: [6, 10], h: [3, 6], p: 0.4 },
    'gullet-basin': { styles: ['shed', 'warehouse'], weights: [3, 2], w: [14, 28], d: [10, 18], h: [5, 12], p: 0.65 },
    'kiln-common': { styles: ['lockup', 'shed', 'garage'], weights: [4, 3, 1], w: [8, 18], d: [6, 10], h: [3, 6], p: 0.35 },
  };
  const backland = (R: Rect, rng: Rng): void => {
  for (let gz = R.minZ + 5; gz < R.maxZ - 5; gz += 8.5) {
    for (let gx = R.minX + 5; gx < R.maxX - 5; gx += 8.5) {
      const p = { x: gx + rng.range(-2, 2), z: gz + rng.range(-2, 2) };
      const rule = BACKFILL[D.districtAt(p)];
      if (!rule || !rng.chance(rule.p)) continue;
      if (insideAnyLot(p, 1.5)) continue;
      let best: { d: number; dir: V2 } | null = null;
      for (let i = 0; i < lotEdges; i++) {
        const m = meta[i]!;
        const d = pointSegmentDistance(p, m.a, m.b);
        if (!best || d < best.d) best = { d, dir: m.dir };
      }
      if (!best || best.d < 8) continue;
      const style = rng.weighted(rule.styles, rule.weights);
      const angle = headingOf(rng.chance(0.5) ? best.dir : leftOf(best.dir));
      for (const f of [1, 0.7, 0.5]) {
        const obb: Obb = { center: p, halfX: (rng.range(rule.w[0], rule.w[1]) * f) / 2, halfZ: Math.max(3, (rng.range(rule.d[0], rule.d[1]) * f) / 2), angle };
        if (!lotValid(obb)) continue;
        const tags = ['backland'];
        if (style === 'factory') tags.push('sawtooth');
        if (style === 'warehouse' && rng.chance(0.3)) tags.push('hoist');
        addLot({ center: p, halfX: obb.halfX, halfZ: obb.halfZ, angle, height: Math.round(rng.range(rule.h[0], rule.h[1]) * 2) / 2, style, tags });
        break;
      }
    }
  }
  };
  backland(CORE, rng);
  /** The old district's lots in generation order, and what became of each (null: displaced by a new street). */
  let oldLots: { orig: BuildingLot; kept: BuildingLot | null }[] = lots.map((orig) => ({ orig, kept: orig }));

  // Kiln Common. The old district's lots were placed blind to the new streets: drop the few that stand
  // where those streets now run (shops and sheds on the north side of Kessmouth Road), then fill the new
  // streets' frontage and backland from Kiln Common's own random stream.
  {
    const keep = lots.map((lot, i) => {
      for (let k = CORE_EDGES; k < edges.length; k++) {
        const e = edges[k]!;
        const m = meta[k]!;
        const clr = e.alley ? e.width / 2 + 0.35 : e.width / 2 + e.sidewalk - 0.02;
        if (pointSegmentDistance(lot.center, m.a, m.b) > clr + Math.hypot(lot.halfX, lot.halfZ)) continue;
        if (polygonSegmentDistance(lotPolys[i]!, m.a, m.b) < clr) return false;
      }
      return true;
    });
    removedLots = lots.filter((_, i) => !keep[i]);
    const kept = lots.filter((_, i) => keep[i]);
    lotPolys = lotPolys.filter((_, i) => keep[i]);
    lotColliders = lotColliders.filter((_, i) => keep[i]);
    const renumbered = kept.map((lot, i) => ({ ...lot, id: i }));
    let k = 0;
    oldLots = lots.map((orig, i) => ({ orig, kept: keep[i] ? renumbered[k++]! : null }));
    lots = renumbered;
    // Every collider so far is a building's: rebuild them with the new ids.
    colliders.length = 0;
    lots.forEach((lot, i) => pushLotCollider(lot, lotColliders[i]!));
  }
  lotEdges = edges.length;
  lotBounds = B;
  const firstNewLot = lots.length;
  for (const l of D.LANDMARKS) {
    if (inCore(l.center)) continue;
    addLot({ center: l.center, halfX: l.halfX, halfZ: l.halfZ, angle: l.angle, height: l.height, style: l.style, tags: [...(l.tags ?? []), `key:${l.key}`], ...(l.name ? { name: l.name } : {}) });
  }
  frontage(byKind(edges.slice(CORE_EDGES).map((e) => e.id)), rngK);
  backland({ minX: B.minX, minZ: B.minZ, maxX: B.maxX, maxZ: CORE.minZ + 8.5 }, rngK);

  // Facade orientation tags: which faces of a lot look onto a street.
  const openFaces = (lot: BuildingLot, nEdges: number): string[] => {
    const out: string[] = [];
    const faces: [string, number, number][] = [
      ['open-front', 0, lot.halfZ],
      ['open-back', 0, -lot.halfZ],
      ['open-left', lot.halfX, 0],
      ['open-right', -lot.halfX, 0],
    ];
    for (const [tag, lx, lz] of faces) {
      const p = obbPoint(lot, lx * 1.0 + Math.sign(lx) * 2.5, lz * 1.0 + Math.sign(lz) * 2.5);
      for (let i = 0; i < nEdges; i++) {
        const e = edges[i]!;
        const m = meta[i]!;
        const lim = e.alley ? e.width / 2 + 3.5 : e.width / 2 + e.sidewalk + 3;
        if (pointSegmentDistance(p, m.a, m.b) < lim) {
          out.push(tag);
          break;
        }
      }
    }
    return out;
  };
  const facade = (lot: BuildingLot, coreTags: string[] | null, kept: BuildingLot | null, rng: Rng): void => {
    if (lot.style === 'arches' || lot.style === 'gasholder') return;
    // Billboards on a terrace's open gable: decided on the faces the old district saw (core view).
    const tags = [...lot.tags, ...(coreTags ?? openFaces(lot, edges.length))];
    if (kept) for (const tag of openFaces(kept, edges.length)) if (!kept.tags.includes(tag)) kept.tags.push(tag);
    if (lot.style === 'terrace' && (tags.includes('open-left') || tags.includes('open-right')) && rng.chance(0.55)) {
      const bb = `billboard:${BILLBOARDS[rng.int(0, BILLBOARDS.length - 1)]}`;
      if (kept) kept.tags.push(bb);
    }
  };
  for (const { orig, kept } of oldLots) facade(orig, openFaces(orig, CORE_EDGES), kept, rng);
  for (let i = firstNewLot; i < lots.length; i++) facade(lots[i]!, null, lots[i]!, rngK);

  // The spray bays (src/garage/bay.ts) need their floor and the way in from the road clear: a filler building
  // standing across one is cut back along its long side (no lot is added or dropped, so every other lot, and
  // everything seeded after them, stays where it was).
  for (const poi of D.POIS) {
    if (poi.kind !== 'resprayShop') continue;
    const bay = bayFor(poi);
    // The bay, the way in, and (the follow camera looks north from the south) a sight line over its south side.
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
    for (const [al, sd] of [[-bay.halfL, -bay.halfW - 0.6], [bay.halfL + 4, -bay.halfW - 0.6], [bay.halfL + 4, bay.halfW + 0.6], [-bay.halfL, bay.halfW + 0.6]] as const) {
      const x = bay.center.x + bay.out.x * al - bay.out.z * sd;
      const z = bay.center.z + bay.out.z * al + bay.out.x * sd;
      x0 = Math.min(x0, x);
      x1 = Math.max(x1, x);
      z0 = Math.min(z0, z);
      z1 = Math.max(z1, z);
    }
    z1 += 8;
    const keep: V2[] = [{ x: x0, z: z0 }, { x: x1, z: z0 }, { x: x1, z: z1 }, { x: x0, z: z1 }];
    for (let i = 0; i < lots.length; i++) {
      const lot = lots[i]!;
      if (lot.tags.includes('respray') || !convexOverlap(obbCorners(lot), keep, 0)) continue;
      const { ax, az } = obbAxes(lot.angle);
      const alongX = lot.halfX >= lot.halfZ;
      const axis = alongX ? ax : az;
      const half = alongX ? lot.halfX : lot.halfZ;
      const c0 = lot.center.x * axis.x + lot.center.z * axis.z;
      let k0 = Infinity;
      let k1 = -Infinity;
      for (const p of keep) {
        const t = p.x * axis.x + p.z * axis.z;
        k0 = Math.min(k0, t);
        k1 = Math.max(k1, t);
      }
      const lo: [number, number] = [c0 - half, Math.min(c0 + half, k0 - 0.3)];
      const hi: [number, number] = [Math.max(c0 - half, k1 + 0.3), c0 + half];
      const [a, b] = lo[1] - lo[0] >= hi[1] - hi[0] ? lo : hi;
      if (b - a < 4) continue;
      const shift = (a + b) / 2 - c0;
      lot.center = { x: lot.center.x + axis.x * shift, z: lot.center.z + axis.z * shift };
      if (alongX) lot.halfX = (b - a) / 2;
      else lot.halfZ = (b - a) / 2;
      lotPolys[i] = obbCorners(lot);
      const col = colliders.find((q) => q.tag === `building:${lot.id}`);
      if (col && col.kind === 'box') {
        col.center = { ...lot.center };
        col.halfX = lot.halfX;
        col.halfZ = lot.halfZ;
      }
    }
  }

  // Safehouse: tag the terrace that the safehouse POI fronts.
  const safePoi = D.POIS.find((p) => p.kind === 'safehouse');
  if (safePoi) {
    let best: BuildingLot | null = null;
    let bestD = Infinity;
    for (const lot of lots) {
      if (lot.style !== 'terrace') continue;
      const d = Math.hypot(lot.center.x - safePoi.pos.x, lot.center.z - safePoi.pos.z);
      if (d < bestD) {
        bestD = d;
        best = lot;
      }
    }
    if (best && bestD < 30) best.tags.push('safehouse');
  }

  // ------------------------------------------------------------------ walls, fences, railings
  const walls: WallDecor[] = [];
  const addWall = (a: V2, b: V2, kind: WallKind, height: number, collide: boolean, thickness = 0.4): void => {
    if (Math.hypot(b.x - a.x, b.z - a.z) < 0.5) return;
    walls.push({ a: { ...a }, b: { ...b }, kind, height });
    if (collide) {
      const blocksSight = kind === 'brick' || kind === 'corrugated';
      colliders.push({ kind: 'segment', a: { ...a }, b: { ...b }, thickness, blocksSight, tag: `wall:${kind}` });
    }
  };
  const nearOtherRoad = (p: V2, except: number): boolean => {
    for (let i = 0; i < (coreView ? CORE_EDGES : edges.length); i++) {
      if (i === except) continue;
      const e = edges[i]!;
      const m = meta[i]!;
      const lim = e.alley ? e.width / 2 + 0.3 : e.width / 2 + e.sidewalk + 0.1;
      if (pointSegmentDistance(p, m.a, m.b) < lim) return true;
    }
    return false;
  };
  const inZone = (p: V2, reasons?: string[]): boolean => D.ZONES.some((z) => (!reasons || reasons.includes(z.reason)) && inRect(z.rect, p));

  // Alley yard walls.
  const yardWalls: WallDecor[] = [];
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i]!;
    const m = meta[i]!;
    for (const side of [1, -1] as const) {
      const rule = side === 1 ? m.left : m.right;
      if (rule.type !== 'yardwall') continue;
      const ns = { x: leftOf(m.dir).x * side, z: leftOf(m.dir).z * side };
      const off = e.width / 2 + 0.25;
      let run: V2[] = [];
      const flush = (): void => {
        if (run.length >= 3) {
          const a = run[0]!;
          const b = run[run.length - 1]!;
          addWall(a, b, 'brick', 2.1, true);
          yardWalls.push(walls[walls.length - 1]!);
        }
        run = [];
      };
      for (let t = 0; t <= m.len; t += 1) {
        const p = { x: m.a.x + m.dir.x * t + ns.x * off, z: m.a.z + m.dir.z * t + ns.z * off };
        const ok = !insideAnyLot(p, 0.35) && !nearOtherRoad(p, i) && !inZone(p, ['viaduct', 'canal + quays']) && inRect(B, p, -1);
        if (ok) run.push(p);
        else flush();
      }
      flush();
    }
  }

  // Terrace yard dividers (visual): from each party wall back to the alley wall.
  const segHit = (o: V2, d: V2, a: V2, b: V2): number => {
    const ex = b.x - a.x;
    const ez = b.z - a.z;
    const den = d.x * ez - d.z * ex;
    if (Math.abs(den) < 1e-9) return Infinity;
    const t = ((a.x - o.x) * ez - (a.z - o.z) * ex) / den;
    const u = ((a.x - o.x) * d.z - (a.z - o.z) * d.x) / den;
    return t > 0 && u >= 0 && u <= 1 ? t : Infinity;
  };
  for (const lot of lots) {
    if (lot.style !== 'terrace') continue;
    const units = Math.max(1, Math.round((lot.halfX * 2) / HOUSE));
    const back = { x: -Math.sin(lot.angle), z: -Math.cos(lot.angle) };
    for (let u = 0; u <= units; u++) {
      const lx = -lot.halfX + (lot.halfX * 2 * u) / units;
      const o = obbPoint(lot, lx, -lot.halfZ);
      let best = 15;
      for (const w of yardWalls) best = Math.min(best, segHit(o, back, w.a, w.b));
      if (best < 15 && best > 1) addWall(o, { x: o.x + back.x * best, z: o.z + back.z * best }, 'gardenWall', 1.5, false);
    }
  }

  // Canal quay railings (gaps at the road bridges), bridge parapets. The railings run on under the
  // viaduct, along the faces of the piers on either bank: a gap there (it used to stop 0.4 m short of
  // each pier corner) is narrower than a character, but the server's 0.6 m facade slack let a modified
  // client slide round the corner into the fenced canal (MOVE-40, docs/SECURITY.md). Railing ends that
  // stop at a building face would leave that slack corridor along the face, so there is no gap at all.
  const bridges: { edgeId: number; z0: number; z1: number }[] = [];
  const quayGaps: [number, number][] = [];
  for (let i = 0; i < edges.length; i++) {
    if (meta[i]!.kind !== 'bridge') continue;
    const e = edges[i]!;
    const m = meta[i]!;
    const hw = e.width / 2 + e.sidewalk + 0.25;
    const bx = m.a.x;
    quayGaps.push([bx - hw, bx + hw]);
    const z0 = D.CANAL.minZ - 3.5;
    const z1 = D.CANAL.maxZ + 3.5;
    bridges.push({ edgeId: i, z0, z1 });
    for (const sx of [-1, 1]) addWall({ x: bx + sx * hw, z: z0 }, { x: bx + sx * hw, z: z1 }, 'parapet', 1.15, true, 0.5);
  }
  quayGaps.sort((a, b) => a[0] - b[0]);
  for (const qz of [D.CANAL.minZ - 0.3, D.CANAL.maxZ + 0.3]) {
    let x = B.minX;
    for (const [g0, g1] of quayGaps) {
      if (g0 > x) addWall({ x, z: qz }, { x: g0, z: qz }, 'railing', 1.1, true, 0.4);
      x = Math.max(x, g1);
    }
    if (x < B.maxX) addWall({ x, z: qz }, { x: B.maxX, z: qz }, 'railing', 1.1, true, 0.4);
  }
  // Basin quay edge (low granite kerb; no railings on a working dock).
  addWall({ x: D.BASIN.minX - 0.3, z: D.BASIN.minZ - 0.3 }, { x: B.maxX, z: D.BASIN.minZ - 0.3 }, 'quayEdge', 0.45, true, 0.6);
  addWall({ x: D.BASIN.minX - 0.3, z: D.BASIN.minZ - 0.3 }, { x: D.BASIN.minX - 0.3, z: B.maxZ }, 'quayEdge', 0.45, true, 0.6);

  // Fenced rectangles with gate gaps.
  const fenceRect = (r: Rect, kind: WallKind, h: number, gates: { side: 'n' | 's' | 'e' | 'w'; from: number; to: number }[]): void => {
    const sides: { side: 'n' | 's' | 'e' | 'w'; a: V2; b: V2; axis: 'x' | 'z' }[] = [
      { side: 'n', a: { x: r.minX, z: r.minZ }, b: { x: r.maxX, z: r.minZ }, axis: 'x' },
      { side: 's', a: { x: r.minX, z: r.maxZ }, b: { x: r.maxX, z: r.maxZ }, axis: 'x' },
      { side: 'w', a: { x: r.minX, z: r.minZ }, b: { x: r.minX, z: r.maxZ }, axis: 'z' },
      { side: 'e', a: { x: r.maxX, z: r.minZ }, b: { x: r.maxX, z: r.maxZ }, axis: 'z' },
    ];
    for (const s of sides) {
      const gs = gates.filter((g) => g.side === s.side).sort((a, b) => a.from - b.from);
      let cur = s.axis === 'x' ? s.a.x : s.a.z;
      const end = s.axis === 'x' ? s.b.x : s.b.z;
      const pt = (v: number): V2 => (s.axis === 'x' ? { x: v, z: s.a.z } : { x: s.a.x, z: v });
      for (const g of gs) {
        addWall(pt(cur), pt(g.from), kind, h, true, 0.35);
        cur = g.to;
      }
      addWall(pt(cur), pt(end), kind, h, true, 0.35);
    }
  };
  fenceRect({ minX: -112, minZ: -235, maxX: -69, maxZ: -113 }, 'palisade', 2.4, [{ side: 'e', from: -182, to: -168 }]);
  fenceRect({ minX: -262, minZ: -5, maxX: -208, maxZ: 120.5 }, 'corrugated', 2.6, [
    { side: 'w', from: 52, to: 68 },
    { side: 's', from: -246, to: -233 },
  ]);
  const PARK: Rect = { minX: 108.2, minZ: -1.8, maxX: 191.8, maxZ: 61.8 };
  fenceRect(PARK, 'railing', 1.2, [
    { side: 'n', from: 156, to: 164 },
    { side: 's', from: 146, to: 154 },
    { side: 'w', from: 26, to: 34 },
    { side: 'e', from: 36, to: 44 },
  ]);
  const CHURCHYARD: Rect = { minX: 207.9, minZ: -50.8, maxX: 262, maxZ: 32 };
  fenceRect(CHURCHYARD, 'railing', 1.4, [{ side: 'w', from: -6.5, to: -3 }]);
  // Kiln Common: the goods yard's palisade (gates onto Kiln Road and Brickfield Lane), the common's
  // railings (a gate on each side), the estate's site hoarding.
  fenceRect(D.KILN_GOODS_YARD, 'palisade', 2.4, [
    { side: 'n', from: -232, to: -214 },
    { side: 'w', from: -381, to: -367 },
  ]);
  const COMMON = D.KILN_COMMON;
  const commonGates = { n: 159, s: 154, w: -391, e: -396 };
  fenceRect(COMMON, 'railing', 1.2, [
    { side: 'n', from: commonGates.n - 4, to: commonGates.n + 4 },
    { side: 's', from: commonGates.s - 4, to: commonGates.s + 4 },
    { side: 'w', from: commonGates.w - 4, to: commonGates.w + 4 },
    { side: 'e', from: commonGates.e - 4, to: commonGates.e + 4 },
  ]);
  fenceRect(D.KILN_ESTATE_SITE, 'corrugated', 2.4, [{ side: 'w', from: -403, to: -393 }]);

  // Front garden walls for terraces with gardens (visual).
  for (const lot of lots) {
    if (lot.style !== 'terrace' || !lot.tags.includes('garden')) continue;
    const a = obbPoint(lot, -lot.halfX, lot.halfZ + 0.8);
    const b = obbPoint(lot, lot.halfX, lot.halfZ + 0.8);
    addWall(a, b, 'gardenWall', 0.7, false);
  }

  // The drive-in spray bay at each respray shop (src/garage/bay.ts): two low block walls either side of the
  // marked floor. They collide (on the server too: it builds the same layout) but hide nothing.
  for (const poi of D.POIS) {
    if (poi.kind !== 'resprayShop') continue;
    for (const [a, b] of bayWalls(bayFor(poi))) {
      walls.push({ a: { ...a }, b: { ...b }, kind: 'gardenWall', height: BAY_WALL_HEIGHT });
      colliders.push({ kind: 'segment', a: { ...a }, b: { ...b }, thickness: 0.35, blocksSight: false, tag: 'wall:sprayBay' });
    }
  }

  // ------------------------------------------------------------------ props
  const props: PropDecor[] = [];
  const loose: LoosePropDecor[] = [];
  const propPts: V2[] = [];
  const freeOfProps = (p: V2, r: number): boolean => propPts.every((q) => Math.hypot(q.x - p.x, q.z - p.z) >= r);
  const addProp = (pr: PropDecor, collider?: { r?: number; box?: [number, number] }): void => {
    props.push(pr);
    propPts.push(pr.pos);
    if (collider?.r) colliders.push({ kind: 'circle', center: { ...pr.pos }, radius: collider.r, blocksSight: false, tag: `prop:${pr.type}` });
    if (collider?.box)
      colliders.push({ kind: 'box', center: { ...pr.pos }, halfX: collider.box[0], halfZ: collider.box[1], angle: pr.heading, blocksSight: false, tag: `prop:${pr.type}` });
  };

  // Zebra crossings + Belisha beacons.
  const zebras: ZebraDecor[] = [];
  for (const [ka, kb, t] of D.ZEBRAS) {
    const e = edgeBetween(ka, kb);
    const m = meta[e.id]!;
    const pos = { x: m.a.x + (m.b.x - m.a.x) * t, z: m.a.z + (m.b.z - m.a.z) * t };
    const l = leftOf(m.dir);
    const beacons: V2[] = [];
    for (const s of [1, -1]) {
      for (const k of [-1.9, 1.9]) {
        const bp = { x: pos.x + l.x * s * (e.width / 2 + 0.45) + m.dir.x * k, z: pos.z + l.z * s * (e.width / 2 + 0.45) + m.dir.z * k };
        beacons.push(bp);
        addProp({ type: 'belisha', pos: bp, heading: 0 }, { r: 0.14 });
      }
    }
    zebras.push({ edgeId: e.id, pos, heading: headingOf(m.dir), width: e.width, beacons });
  }
  const nearZebra = (p: V2, r: number): boolean => zebras.some((z) => Math.hypot(z.pos.x - p.x, z.pos.z - p.z) < r);

  // Traffic signal heads: one per approach, on the nearside (left) kerb before the stop line.
  const signalHeads: SignalHeadDecor[] = [];
  for (const it of intersections) {
    if (!it.signal) continue;
    for (const id of it.edgeIds) {
      const e = edges[id]!;
      const intoNode = e.to === it.nodeId;
      if (e.oneWay && !intoNode) continue;
      const out = dirFrom(it.nodeId, e);
      const a = { x: -out.x, z: -out.z };
      const l = leftOf(a);
      const pos = {
        x: it.pos.x - a.x * (it.radius + 0.6) + l.x * (e.width / 2 + 0.55),
        z: it.pos.z - a.z * (it.radius + 0.6) + l.z * (e.width / 2 + 0.55),
      };
      signalHeads.push({ intersectionId: it.id, edgeId: id, pos, heading: headingOf(out) });
      propPts.push(pos);
      colliders.push({ kind: 'circle', center: { ...pos }, radius: 0.16, blocksSight: false, tag: 'prop:signal' });
    }
  }

  // Phone boxes and pillar boxes.
  for (const p of D.POIS) {
    if (p.kind !== 'phonebox') continue;
    addProp({ type: 'phoneBox', pos: { ...p.pos }, heading: p.heading }, { box: [0.55, 0.55] });
  }
  for (const pos of [
    { x: -8.4, z: -146 },
    { x: 106.6, z: 52 },
    { x: 121, z: -148.6 },
    { x: 8.3, z: -205 },
    { x: -55, z: 202.2 },
    { x: -8.4, z: -330 },
  ]) {
    addProp({ type: 'pillarBox', pos, heading: 0 }, { r: 0.32 });
  }

  // Street lamps. The old streets' in the core view (then dropped where a new street or lot now is).
  const onKilnStreetOrLot = (p: V2, except: number, pad: number): boolean => {
    const was = coreView;
    const wasEdges = lotEdges;
    coreView = false;
    lotEdges = edges.length;
    const hit = nearOtherRoad(p, except) || insideAnyLot(p, pad);
    coreView = was;
    lotEdges = wasEdges;
    return hit;
  };
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i]!;
    const m = meta[i]!;
    const core = i < CORE_EDGES;
    const rr = core ? rng : rngK;
    coreView = core;
    lotEdges = core ? CORE_EDGES : edges.length;
    if (e.alley) {
      // Bracket lamps on the alley walls (no collider), alternating sides.
      let k = 0;
      for (let t = 10; t < m.len - 6; t += 22, k++) {
        const side = k % 2 === 0 ? 1 : -1;
        const ns = { x: leftOf(m.dir).x * side, z: leftOf(m.dir).z * side };
        const p = { x: m.a.x + m.dir.x * t + ns.x * (e.width / 2 + 0.15), z: m.a.z + m.dir.z * t + ns.z * (e.width / 2 + 0.15) };
        if (nearOtherRoad(p, i)) continue;
        props.push({ type: 'wallLamp', pos: p, heading: headingOf({ x: -ns.x, z: -ns.z }) });
      }
      continue;
    }
    if (e.sidewalk < 1.5) continue;
    const bridge = m.kind === 'bridge';
    const spacing = bridge ? 17 : 27;
    for (const side of [1, -1] as const) {
      const ns = { x: leftOf(m.dir).x * side, z: leftOf(m.dir).z * side };
      const t0 = sideClearance(e.from, e, ns) + 5;
      const t1 = m.len - sideClearance(e.to, e, ns) - 4;
      const off = e.width / 2 + 0.55;
      for (let t = t0 + (side === 1 ? 0 : spacing / 2); t <= t1; t += spacing) {
        const p = { x: m.a.x + m.dir.x * t + ns.x * off, z: m.a.z + m.dir.z * t + ns.z * off };
        if (inRect(D.SPAWN_CLEAR, p)) continue;
        if (bridge && !inRect(D.CANAL, p, 1)) continue;
        if (!bridge && inZone(p, ['canal + quays'])) continue;
        if (nearZebra(p, 4) || !freeOfProps(p, 2.2) || insideAnyLot(p, 0.3) || nearOtherRoad(p, i)) continue;
        const variant = rr.int(0, 3);
        if (core && onKilnStreetOrLot(p, i, 0.3)) continue;
        addProp({ type: bridge ? 'bridgeLamp' : 'lamp', pos: p, heading: headingOf({ x: -ns.x, z: -ns.z }), variant }, { r: 0.18 });
      }
    }
  }
  coreView = false;
  lotEdges = edges.length;

  // Canal-side trees, lamps and benches on the quay strips.
  for (const [qz, faceDir] of [
    [D.CANAL.minZ - 4.5, -1],
    [D.CANAL.maxZ + 4.5, 1],
  ] as const) {
    let k = 0;
    for (let x = -292; x <= 292; x += 12, k++) {
      const p = { x, z: qz };
      if (quayGaps.some(([g0, g1]) => x > g0 - 4 && x < g1 + 4)) continue;
      if (qz > 170 && x > 200) continue; // dock apron: working quay, no trees
      if (k % 3 === 0) addProp({ type: 'tree', pos: p, heading: rng.range(0, 6.28), scale: rng.range(0.85, 1.15) }, { r: 0.4 });
      else if (k % 3 === 1) addProp({ type: 'lamp', pos: { x, z: qz + faceDir * 2.6 }, heading: faceDir > 0 ? Math.PI : 0, variant: 1 }, { r: 0.18 });
      else props.push({ type: 'bench', pos: { x, z: qz + faceDir * 2.2 }, heading: faceDir > 0 ? 0 : Math.PI });
    }
  }
  // Mooring bollards along the basin and canal edges.
  for (let x = D.BASIN.minX + 4; x < 298; x += 9) props.push({ type: 'mooring', pos: { x, z: D.BASIN.minZ - 1.2 }, heading: 0 });
  for (let z = D.BASIN.minZ + 5; z < 298; z += 9) props.push({ type: 'mooring', pos: { x: D.BASIN.minX - 1.2, z }, heading: Math.PI / 2 });

  // Mercy Square gardens: perimeter plane trees, bandstand, memorial, benches, paths.
  const parkCenter = { x: 150, z: 30 };
  addProp({ type: 'bandstand', pos: parkCenter, heading: 0 }, { r: 4.6 });
  const gates = [
    { x: 160, z: -1.8 },
    { x: 150, z: 61.8 },
    { x: 108.2, z: 30 },
    { x: 191.8, z: 40 },
  ];
  const parkPaths = gates.map((g) => ({ a: g, b: parkCenter, width: 3.2 }));
  const onPath = (p: V2, pad: number): boolean => parkPaths.some((pp) => pointSegmentDistance(p, pp.a, pp.b) < pp.width / 2 + pad);
  for (let x = PARK.minX + 4; x <= PARK.maxX - 4; x += 10.5) {
    for (const z of [PARK.minZ + 3.5, PARK.maxZ - 3.5]) {
      const p = { x, z };
      if (!onPath(p, 2.5)) addProp({ type: 'tree', pos: p, heading: rng.range(0, 6.28), scale: rng.range(1.0, 1.3) }, { r: 0.45 });
    }
  }
  for (let z = PARK.minZ + 12; z <= PARK.maxZ - 10; z += 10.5) {
    for (const x of [PARK.minX + 3.5, PARK.maxX - 3.5]) {
      const p = { x, z };
      if (!onPath(p, 2.5)) addProp({ type: 'tree', pos: p, heading: rng.range(0, 6.28), scale: rng.range(1.0, 1.3) }, { r: 0.45 });
    }
  }
  for (const p of [
    { x: 128, z: 14 },
    { x: 174, z: 15 },
    { x: 127, z: 47 },
    { x: 172, z: 49 },
  ]) {
    addProp({ type: 'tree', pos: p, heading: rng.range(0, 6.28), scale: 1.35 }, { r: 0.5 });
  }
  addProp({ type: 'memorial', pos: { x: 150, z: 8 }, heading: Math.PI }, { r: 1.2 });
  for (const pp of parkPaths) {
    const mid = { x: (pp.a.x + pp.b.x) / 2, z: (pp.a.z + pp.b.z) / 2 };
    const d = { x: pp.b.x - pp.a.x, z: pp.b.z - pp.a.z };
    const l = Math.hypot(d.x, d.z);
    const n = { x: -d.z / l, z: d.x / l };
    props.push({ type: 'bench', pos: { x: mid.x + n.x * 2.6, z: mid.z + n.z * 2.6 }, heading: headingOf({ x: -n.x, z: -n.z }) });
  }

  // Kiln Common: a green with paths from each gate to the war memorial, plane trees round the edge,
  // benches. Its own random stream (see rngK).
  const commonCenter = { x: 160, z: -392 };
  const commonPaths = [
    { a: { x: commonGates.n, z: COMMON.minZ }, b: commonCenter, width: 3 },
    { a: { x: commonGates.s, z: COMMON.maxZ }, b: commonCenter, width: 3 },
    { a: { x: COMMON.minX, z: commonGates.w }, b: commonCenter, width: 3 },
    { a: { x: COMMON.maxX, z: commonGates.e }, b: commonCenter, width: 3 },
  ];
  const onCommonPath = (p: V2, pad: number): boolean => commonPaths.some((pp) => pointSegmentDistance(p, pp.a, pp.b) < pp.width / 2 + pad);
  addProp({ type: 'memorial', pos: commonCenter, heading: Math.PI }, { r: 1.2 });
  for (let x = COMMON.minX + 4; x <= COMMON.maxX - 4; x += 11) {
    for (const z of [COMMON.minZ + 3.5, COMMON.maxZ - 3.5]) {
      const p = { x, z };
      if (!onCommonPath(p, 2.5)) addProp({ type: 'tree', pos: p, heading: rngK.range(0, 6.28), scale: rngK.range(1.0, 1.3) }, { r: 0.45 });
    }
  }
  for (let z = COMMON.minZ + 14; z <= COMMON.maxZ - 10; z += 11) {
    for (const x of [COMMON.minX + 3.5, COMMON.maxX - 3.5]) {
      const p = { x, z };
      if (!onCommonPath(p, 2.5)) addProp({ type: 'tree', pos: p, heading: rngK.range(0, 6.28), scale: rngK.range(1.0, 1.3) }, { r: 0.45 });
    }
  }
  // A loose clump of old oaks on the open grass (where the brick kilns stood).
  for (const p of [
    { x: 128, z: -414 },
    { x: 136, z: -408 },
    { x: 190, z: -372 },
    { x: 197, z: -413 },
    { x: 126, z: -370 },
  ]) {
    addProp({ type: 'tree', pos: p, heading: rngK.range(0, 6.28), scale: rngK.range(1.25, 1.5) }, { r: 0.5 });
  }
  for (const pp of commonPaths) {
    const mid = { x: (pp.a.x + pp.b.x) / 2, z: (pp.a.z + pp.b.z) / 2 };
    const d = { x: pp.b.x - pp.a.x, z: pp.b.z - pp.a.z };
    const l = Math.hypot(d.x, d.z);
    const n = { x: -d.z / l, z: d.x / l };
    props.push({ type: 'bench', pos: { x: mid.x + n.x * 2.5, z: mid.z + n.z * 2.5 }, heading: headingOf({ x: -n.x, z: -n.z }) });
  }
  parkPaths.push(...commonPaths);

  // Churchyard: yews and rows of headstones.
  for (const p of [
    { x: 219, z: 14 },
    { x: 246, z: 22 },
    { x: 258, z: -44 },
    { x: 214, z: -40 },
    { x: 257, z: 6 },
    { x: 232, z: -42 },
  ]) {
    addProp({ type: 'yew', pos: p, heading: rng.range(0, 6.28), scale: rng.range(0.9, 1.2) }, { r: 0.6 });
  }
  for (let z = 6; z <= 28; z += 2.6) {
    for (let x = 222; x <= 258; x += 1.9) {
      if (rng.chance(0.18)) continue;
      const p = { x: x + rng.range(-0.25, 0.25), z: z + rng.range(-0.2, 0.2) };
      if (!freeOfProps(p, 1.4)) continue;
      props.push({ type: 'grave', pos: p, heading: -Math.PI / 2 + rng.range(-0.12, 0.12), variant: rng.int(0, 3) });
    }
  }
  for (let z = -46; z <= -27; z += 2.6) {
    for (let x = 220; x <= 258; x += 1.9) {
      if (rng.chance(0.25)) continue;
      const p = { x: x + rng.range(-0.25, 0.25), z: z + rng.range(-0.2, 0.2) };
      if (!freeOfProps(p, 1.4)) continue;
      props.push({ type: 'grave', pos: p, heading: -Math.PI / 2 + rng.range(-0.12, 0.12), variant: rng.int(0, 3) });
    }
  }

  // Scrap yard piles (crushed car stacks), tyres.
  const scrapPiles: Decor['scrapPiles'] = [];
  for (const [cx, cz, hx, hz, h] of [
    [-252, 8, 6, 9, 3.4],
    [-252, 30, 6, 8, 2.6],
    [-218, 10, 6, 10, 3.0],
    [-218, 40, 6, 9, 3.8],
    [-252, 92, 6, 12, 3.2],
    [-218, 80, 6, 11, 2.8],
    [-236, 108, 9, 5, 2.4],
  ] as const) {
    const pile = { center: { x: cx, z: cz }, halfX: hx, halfZ: hz, angle: 0, height: h };
    scrapPiles.push(pile);
    colliders.push({ kind: 'box', center: { x: cx, z: cz }, halfX: hx, halfZ: hz, angle: 0, blocksSight: true, tag: 'scrap' });
  }
  for (const p of [
    { x: -240, z: 20 },
    { x: -230, z: 62 },
    { x: -244, z: 70 },
    { x: -226, z: 100 },
  ]) {
    addProp({ type: 'tyres', pos: p, heading: rng.range(0, 6.28) }, { r: 0.7 });
  }

  // Dock apron: drum stacks (static) and loose crates/barrels.
  for (const p of [
    { x: 214, z: 186 },
    { x: 251, z: 183 },
    { x: 288, z: 200 },
    { x: 120, z: 207 },
  ]) {
    addProp({ type: 'drums', pos: p, heading: rng.range(0, 6.28) }, { r: 1.2 });
  }
  const crateCluster = (c: V2, n: number, type: 'crate' | 'barrel' | 'box' = 'crate'): void => {
    for (let i = 0; i < n; i++) {
      const p = { x: c.x + rng.range(-3, 3), z: c.z + rng.range(-2.2, 2.2) };
      if (loose.some((q) => Math.hypot(q.pos.x - p.x, q.pos.z - p.z) < 1.25)) continue;
      loose.push({ type: rng.chance(0.25) ? 'barrel' : type, pos: p, heading: rng.range(-0.3, 0.3) });
    }
  };
  crateCluster({ x: 238, z: 197 }, 8);
  crateCluster({ x: 212, z: 205 }, 6);
  crateCluster({ x: 277, z: 193 }, 6);
  crateCluster({ x: 150, z: 210 }, 6);
  crateCluster({ x: 95, z: 232 }, 5);
  crateCluster({ x: -236, z: 64 }, 5, 'box');
  crateCluster({ x: -86, z: -178 }, 5, 'barrel');

  // Dustbins in alleys (against the walls) and outside shops.
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i]!;
    const m = meta[i]!;
    if (!e.alley) continue;
    // Old alleys in the core view (main stream), Kiln Common's from its own stream.
    const core = i < CORE_EDGES;
    const rng = core ? rngMain : rngK;
    coreView = core;
    for (let t = 7; t < m.len - 7; t += rng.range(9, 17)) {
      const side = rng.chance(0.5) ? 1 : -1;
      const ns = { x: leftOf(m.dir).x * side, z: leftOf(m.dir).z * side };
      const p = { x: m.a.x + m.dir.x * t + ns.x * (e.width / 2 - 0.45), z: m.a.z + m.dir.z * t + ns.z * (e.width / 2 - 0.45) };
      if (nearOtherRoad(p, i) || inZone(p, ['viaduct'])) continue;
      const bin: LoosePropDecor = { type: 'bin', pos: p, heading: rng.range(0, 6.28) };
      const extra: LoosePropDecor | null = rng.chance(0.4) ? { type: rng.chance(0.5) ? 'bin' : 'box', pos: { x: p.x + m.dir.x * 0.75, z: p.z + m.dir.z * 0.75 }, heading: rng.range(0, 6.28) } : null;
      if (core && onKilnStreetOrLot(p, i, 0)) continue;
      loose.push(bin);
      if (extra) loose.push(extra);
    }
  }
  coreView = false;
  const shopBin = (lot: BuildingLot, keep: boolean, rng: Rng): void => {
    if (lot.style !== 'shopfront' && lot.style !== 'cafe' && lot.style !== 'pub') return;
    if (!rng.chance(0.4)) return;
    const p = obbPoint(lot, lot.halfX - 0.7, lot.halfZ + 0.55);
    if (inRect(D.SPAWN_CLEAR, p, 1) || !freeOfProps(p, 1.5)) return;
    const heading = rng.range(0, 6.28);
    if (keep) loose.push({ type: 'bin', pos: p, heading });
  };
  for (const { orig, kept } of oldLots) shopBin(orig, kept !== null, rng);
  for (let i = firstNewLot; i < lots.length; i++) shopBin(lots[i]!, true, rngK);
  // Roadworks on Bell Street: a ring of cones.
  for (let k = 0; k < 7; k++) {
    const a = (k / 7) * Math.PI * 2;
    loose.push({ type: 'cone', pos: { x: 70 + Math.cos(a) * 2.2, z: -152.5 + Math.sin(a) * 1.4 }, heading: 0 });
  }
  // … closed off by a striped roadworks barrier across the lane at either end
  for (const dx of [-3.4, 3.4]) loose.push({ type: 'barrier', pos: { x: 70 + dx, z: -152.5 }, heading: Math.PI / 2 });

  // Cranes on the basin quay, a coaster moored in the basin.
  const cranes = [
    { pos: { x: 132, z: 211 }, heading: Math.PI },
    { pos: { x: 178, z: 211 }, heading: Math.PI * 0.92 },
    { pos: { x: 244, z: 211 }, heading: Math.PI * 1.1 },
  ];
  for (const c of cranes) {
    for (const [lx, lz] of [
      [-3.4, -3.2],
      [3.4, -3.2],
      [-3.4, 3.2],
      [3.4, 3.2],
    ] as const) {
      colliders.push({ kind: 'circle', center: { x: c.pos.x + lx, z: c.pos.z + lz }, radius: 0.45, blocksSight: false, tag: 'prop:crane' });
    }
  }
  const ships = [{ pos: { x: 186, z: 236 }, heading: Math.PI / 2, length: 62, name: 'MERROW TRADER' }];

  // Container and timber stacks on the dock apron: cover and a little maze for chases.
  const stacks: Decor['stacks'] = [];
  const addStack = (cx: number, cz: number, angle: number, tiers: number, kind: 'container' | 'timber', len: number): void => {
    const hx = len / 2;
    const hz = kind === 'container' ? 1.25 : 1.4;
    stacks.push({ center: { x: cx, z: cz }, halfX: hx, halfZ: hz, angle, tiers, kind, seed: stacks.length });
    colliders.push({ kind: 'box', center: { x: cx, z: cz }, halfX: hx, halfZ: hz, angle, blocksSight: tiers > 1, tag: `stack:${kind}` });
  };
  for (const [cx, cz, angle, tiers, kind, len] of [
    [222, 187, 0, 2, 'container', 12.2],
    [222, 196, 0, 1, 'container', 12.2],
    [262, 184, Math.PI / 2, 2, 'container', 6.1],
    [270, 184, Math.PI / 2, 3, 'container', 6.1],
    [286, 203, 0, 2, 'container', 12.2],
    [255, 200, 0, 1, 'timber', 7],
    [240, 186, 0, 1, 'timber', 6],
  ] as const) {
    addStack(cx, cz, angle, tiers, kind, len);
  }
  // Kiln Road Goods Depot: timber and a couple of containers waiting on the setts, crates by the shed,
  // and the estate site's stacked timber and drums.
  for (const [cx, cz, angle, tiers, kind, len] of [
    [-240, -350, 0, 2, 'timber', 8],
    [-240, -340, 0, 1, 'timber', 7],
    [-212, -352, Math.PI / 2, 2, 'container', 6.1],
    [-200, -352, Math.PI / 2, 1, 'container', 6.1],
    [-176, -330, 0, 2, 'container', 12.2],
    [-226, -322, 0, 1, 'timber', 6],
    [275, -374, 0, 1, 'timber', 6],
  ] as const) {
    addStack(cx, cz, angle, tiers, kind, len);
  }
  for (const p of [
    { x: -160, z: -410 },
    { x: 268, z: -376 },
    { x: 290, z: -428 },
  ]) {
    addProp({ type: 'drums', pos: p, heading: rngK.range(0, 6.28) }, { r: 1.2 });
  }
  for (const [c, n, t] of [
    [{ x: -205, z: -368 }, 6, 'crate'],
    [{ x: -168, z: -368 }, 5, 'crate'],
    [{ x: -250, z: -400 }, 4, 'barrel'],
    [{ x: 266, z: -415 }, 4, 'box'],
  ] as const) {
    for (let i = 0; i < n; i++) {
      const p = { x: c.x + rngK.range(-3, 3), z: c.z + rngK.range(-2.2, 2.2) };
      if (loose.some((q) => Math.hypot(q.pos.x - p.x, q.pos.z - p.z) < 1.25)) continue;
      loose.push({ type: rngK.chance(0.25) ? 'barrel' : t, pos: p, heading: rngK.range(-0.3, 0.3) });
    }
  }

  // Street name plates on corner buildings near junctions (one per edge end).
  const streetPlates: Decor['streetPlates'] = [];
  for (const it of intersections) {
    const oldJunction = coreJunction(nodes[it.nodeId]!);
    for (const id of it.edgeIds) {
      const e = edges[id]!;
      // An old junction's old streets draw from the main stream, as before Kiln Common.
      const rng = oldJunction && id < CORE_EDGES ? rngMain : rngK;
      if (e.alley && rng.chance(0.5)) continue;
      const out = dirFrom(it.nodeId, e);
      const l = leftOf(out);
      const side = rng.chance(0.5) ? 1 : -1;
      const off = e.alley ? e.width / 2 + 0.05 : e.width / 2 + e.sidewalk + 0.05;
      const along = it.radius + 4;
      const p = { x: it.pos.x + out.x * along + l.x * side * off, z: it.pos.z + out.z * along + l.z * side * off };
      if (!insideAnyLot({ x: p.x + l.x * side * 0.4, z: p.z + l.z * side * 0.4 }, 0)) continue;
      streetPlates.push({ pos: p, heading: headingOf({ x: -l.x * side, z: -l.z * side }), text: e.name.toUpperCase() });
    }
  }

  // ------------------------------------------------------------------ boundary
  colliders.push({ kind: 'segment', a: { x: B.minX, z: B.minZ }, b: { x: B.maxX, z: B.minZ }, thickness: 2, tag: 'boundary' });
  colliders.push({ kind: 'segment', a: { x: B.minX, z: B.maxZ }, b: { x: B.maxX, z: B.maxZ }, thickness: 2, tag: 'boundary' });
  colliders.push({ kind: 'segment', a: { x: B.minX, z: B.minZ }, b: { x: B.minX, z: B.maxZ }, thickness: 2, tag: 'boundary' });
  colliders.push({ kind: 'segment', a: { x: B.maxX, z: B.minZ }, b: { x: B.maxX, z: B.maxZ }, thickness: 2, tag: 'boundary' });

  // ------------------------------------------------------------------ parked spots
  const parkedSpots: ParkedSpot[] = [];
  // Junction clearance at a street end: an old street sees the junctions the old district had.
  const parkClear = (nodeId: number, core: boolean): number => {
    const ixId = nodes[nodeId]!.intersectionId;
    if (ixId === null || (core && !coreJunction(nodes[nodeId]!))) return 3;
    return intersections[ixId]!.radius + 5;
  };
  const kilnJunctions = intersections.filter((it) => !coreJunction(nodes[it.nodeId]!));
  const sprayBays = D.POIS.filter((p) => p.kind === 'resprayShop').map(bayFor);
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i]!;
    const m = meta[i]!;
    if (m.spec.parking <= 0) continue;
    const core = i < CORE_EDGES;
    const rng = core ? rngMain : rngK;
    const jA = parkClear(e.from, core);
    const jB = parkClear(e.to, core);
    for (const side of [1, -1] as const) {
      const l = leftOf(m.dir);
      const off = e.width / 2 - m.spec.parking / 2;
      const fwd = e.oneWay || side === 1;
      const heading = headingOf(fwd ? m.dir : { x: -m.dir.x, z: -m.dir.z });
      for (let t = jA + 3; t <= m.len - jB - 3; t += 6.3) {
        const p = { x: m.a.x + m.dir.x * t + l.x * side * off, z: m.a.z + m.dir.z * t + l.z * side * off };
        if (inRect(D.SPAWN_CLEAR, p, 1) || nearZebra(p, 9) || inZone(p, ['canal + quays'])) continue;
        if (rng.chance(0.12)) continue;
        // Old bays that now sit in the mouth of a Kiln Common junction stay empty.
        if (core && kilnJunctions.some((it) => Math.hypot(p.x - it.pos.x, p.z - it.pos.z) < it.radius + 5)) continue;
        const district = e.districtId;
        const spot: ParkedSpot = { pos: p, heading, districtId: district, allowedTypes: district === 'gullet-basin' ? ['van', 'saloon', 'compact'] : [] };
        // Nobody parks across the spray bay's mouth. (Kept in the list, flagged, so every other spot keeps its index and seed.)
        if (sprayBays.some((b) => blocksBayApproach(b, p))) spot.reserved = true;
        parkedSpots.push(spot);
      }
    }
  }

  // ------------------------------------------------------------------ faces: kerbs, pavements, ped loops
  const adj: { other: number; edge: number; ang: number }[][] = nodes.map(() => []);
  for (const e of edges) {
    const a = nodes[e.from]!.pos;
    const b = nodes[e.to]!.pos;
    adj[e.from]!.push({ other: e.to, edge: e.id, ang: Math.atan2(b.z - a.z, b.x - a.x) });
    adj[e.to]!.push({ other: e.from, edge: e.id, ang: Math.atan2(a.z - b.z, a.x - b.x) });
  }
  for (const l of adj) l.sort((p, q) => p.ang - q.ang);
  const visited = new Set<string>();
  const rawFaces: { nodes: number[]; edges: number[]; area: number }[] = [];
  for (const e of edges) {
    for (const [u0, v0] of [
      [e.from, e.to],
      [e.to, e.from],
    ] as const) {
      if (visited.has(`${u0}>${v0}`)) continue;
      const fn: number[] = [];
      const fe: number[] = [];
      let u = u0;
      let v = v0;
      let edgeId = e.id;
      let guard = 0;
      while (!visited.has(`${u}>${v}`) && guard++ < 500) {
        visited.add(`${u}>${v}`);
        fn.push(u);
        fe.push(edgeId);
        const list = adj[v]!;
        const idx = list.findIndex((x) => x.other === u && x.edge === edgeId);
        const nx = list[(idx - 1 + list.length) % list.length]!;
        u = v;
        v = nx.other;
        edgeId = nx.edge;
      }
      rawFaces.push({ nodes: fn, edges: fe, area: polygonArea(fn.map((n) => nodes[n]!.pos)) });
    }
  }
  let outerIdx = 0;
  rawFaces.forEach((f, i) => {
    if (Math.abs(f.area) > Math.abs(rawFaces[outerIdx]!.area)) outerIdx = i;
  });
  const interiorSign = -Math.sign(rawFaces[outerIdx]!.area);
  const faceSide: 1 | -1 = interiorSign > 0 ? -1 : 1;

  const faces: FaceDecor[] = [];
  const pedPaths: PedPath[] = [];
  // Alley corridors (carriageway + the 0.25-0.4 m strip to the walls) that ped paths must avoid.
  const alleyMeta = edges.filter((e) => e.alley).map((e) => ({ a: nodes[e.from]!.pos, b: nodes[e.to]!.pos, half: e.width / 2 + 0.5 }));
  rawFaces.forEach((f, fi) => {
    const pts = f.nodes.map((n) => nodes[n]!.pos);
    const kerbD = f.edges.map((id) => edges[id]!.width / 2);
    const lineD = f.edges.map((id) => edges[id]!.width / 2 + edges[id]!.sidewalk);
    const pedD = f.edges.map((id) => {
      const e = edges[id]!;
      return e.sidewalk > 0 ? e.width / 2 + e.sidewalk * 0.5 : e.width / 2 - 0.6;
    });
    const n = f.edges.length;
    const kr: number[] = [];
    for (let i = 0; i < n; i++) {
      const ein = f.edges[(i - 1 + n) % n]!;
      const eout = f.edges[i]!;
      kr.push(Math.min(meta[ein]!.spec.kerbRadius, meta[eout]!.spec.kerbRadius));
    }
    const lineR = kr.map((r, i) => Math.max(0.3, r - Math.min(lineD[i]! - kerbD[i]!, lineD[(i - 1 + n) % n]! - kerbD[(i - 1 + n) % n]!)));
    const pedR = kr.map((r) => Math.max(0.3, r - 1));
    const outer = fi === outerIdx;
    const kerb = simplifyClosed(offsetFace(pts, kerbD, kr, faceSide));
    const line = simplifyClosed(offsetFace(pts, lineD, lineR, faceSide));
    const ped = offsetFace(pts, pedD, pedR, faceSide, 3);
    const c = pts.reduce((acc, p) => ({ x: acc.x + p.x / pts.length, z: acc.z + p.z / pts.length }), { x: 0, z: 0 });
    const district = D.districtAt(c);
    faces.push({ kerb, line, outer, district });
    const loopPts = resampleClosed(simplifyClosed(ped, 0.02), 10);
    const crossings: number[] = [];
    for (const nIdx of f.nodes) {
      if (incident[nIdx]!.length < 3) continue;
      const np = nodes[nIdx]!.pos;
      let best = -1;
      let bd = Infinity;
      loopPts.forEach((p, k) => {
        const d = Math.hypot(p.x - np.x, p.z - np.z);
        if (d < bd) {
          bd = d;
          best = k;
        }
      });
      if (best >= 0 && !crossings.includes(best)) crossings.push(best);
    }
    crossings.sort((a, b) => a - b);
    // Alleys have no pavement (buildings/yard walls stand 0.25-0.4 m off the carriageway edge), so
    // a ped loop along one would put walkers in the car's lane. Drop every loop point inside an
    // alley corridor and keep the remaining pavement stretches as open (ping-pong) paths; the
    // stretch ends at an alley mouth become crossing points, so peds cross alleys explicitly.
    const inAlley = loopPts.map((p) => alleyMeta.some((m) => pointSegmentDistance(p, m.a, m.b) < m.half));
    if (!inAlley.some(Boolean)) {
      pedPaths.push({ id: pedPaths.length, points: loopPts, loop: true, districtId: district, crossings });
      return;
    }
    const n2 = loopPts.length;
    const start = inAlley.indexOf(true);
    const runs: number[][] = [];
    let run: number[] = [];
    for (let k = 1; k <= n2; k++) {
      const idx = (start + k) % n2;
      if (inAlley[idx]) {
        if (run.length) runs.push(run);
        run = [];
      } else run.push(idx);
    }
    if (run.length) runs.push(run);
    for (const r of runs) {
      if (r.length < 3) continue;
      const pts2 = r.map((k) => loopPts[k]!);
      const cr = new Set<number>([0, pts2.length - 1]);
      for (const c of crossings) {
        const j = r.indexOf(c);
        if (j >= 0) cr.add(j);
      }
      pedPaths.push({ id: pedPaths.length, points: pts2, loop: false, districtId: district, crossings: [...cr].sort((a, b) => a - b) });
    }
  });

  // Grass patches.
  const grass: V2[][] = [
    rectPoly({ minX: PARK.minX + 0.4, minZ: PARK.minZ + 0.4, maxX: PARK.maxX - 0.4, maxZ: PARK.maxZ - 0.4 }),
    rectPoly({ minX: CHURCHYARD.minX + 0.4, minZ: CHURCHYARD.minZ + 0.4, maxX: CHURCHYARD.maxX - 0.4, maxZ: CHURCHYARD.maxZ - 0.4 }),
    rectPoly({ minX: COMMON.minX + 0.4, minZ: COMMON.minZ + 0.4, maxX: COMMON.maxX - 0.4, maxZ: COMMON.maxZ - 0.4 }),
  ];

  // ------------------------------------------------------------------ POIs
  const pois: PointOfInterest[] = D.POIS.map((p) => ({ ...p, pos: { ...p.pos }, districtId: D.districtAt(p.pos) }));

  const layout: CityLayout = {
    name: 'Kessmouth — Low Borough',
    bounds: { ...B },
    districts: D.DISTRICTS.map((d) => ({ ...d, bounds: { ...d.bounds }, character: [...d.character] })),
    roads: { nodes, edges },
    intersections,
    buildings: lots,
    colliders,
    parkedSpots,
    pedPaths,
    pois,
    playerSpawn: { pos: { ...D.PLAYER_SPAWN.pos }, heading: D.PLAYER_SPAWN.heading },
    features: mapFeatures(B),
  };

  const decor: Decor = {
    faces,
    setts,
    props,
    looseProps: loose,
    walls,
    signalHeads,
    zebras,
    viaduct: { x: vx, halfWidth: D.VIADUCT.halfWidth, deckHeight: D.VIADUCT.deckHeight, spans, openings },
    gasholders,
    cranes,
    ships,
    water: D.WATER.map((w) => ({ ...w })),
    bridges,
    grass,
    parkPaths,
    scrapPiles,
    stacks,
    streetPlates,
  };
  void pointInPolygon;
  return { layout, decor };
}
